import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiClipboard,
  FiPlus,
  FiSearch,
  FiGrid,
  FiList,
  FiRotateCcw,
  FiInbox,
  FiEdit2,
  FiTrash2,
  FiMapPin,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification, { showToast } from "../../components/common/notification/Notification";
import Spinner from "../../components/common/spinner/Spinner";
import { usePropertyApiClient } from "../property/services/usePropertyApiClient";
import usePersistentState from "../../hooks/usePersistentState";
import { createLeadService } from "./services/leadService";
import { LEAD_STATUSES, LEAD_DEFAULT_PAGE_SIZE, LEAD_FETCH_PAGE_SIZE } from "./constants/leadEndpoints";
import "../property/PropertiesPage.css";
import "../property/components/PropertyPageHeader.css";
import "../property/components/PropertyFilterToolbar.css";
import "../lease/LeasesPage.css";

const CREATE_PATH = "/customer/leads/new";

const statusMeta = (status) =>
  LEAD_STATUSES.find((s) => s.value === String(status || "").toUpperCase()) || null;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : "";

const StatusBadge = ({ status }) => {
  const meta = statusMeta(status);
  return meta ? <span className={`badge bg-${meta.variant}${meta.variant === "warning" ? " text-dark" : ""}`}>{meta.label}</span> : null;
};

/** /customer/leads - the logged-in customer's own leads (same endpoint as the mobile Leads tab) */
const LeadsPage = () => {
  const navigate = useNavigate();
  const apiClient = usePropertyApiClient();
  const leadService = useMemo(() => createLeadService(apiClient), [apiClient]);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purposes, setPurposes] = useState([]);

  const [currentPage, setCurrentPage] = usePersistentState("list:leads:page", 1);
  const [searchTerm, setSearchTerm] = usePersistentState("list:leads:search", "");
  const [selectedStatus, setSelectedStatus] = usePersistentState("list:leads:status", "");
  const [selectedPurpose, setSelectedPurpose] = usePersistentState("list:leads:purpose", "");
  const [viewMode, setViewMode] = usePersistentState("list:leads:view", "grid");

  const [leadToDelete, setLeadToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    leadService.fetchPurposes().then(setPurposes).catch(() => setPurposes([]));
  }, [leadService]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await leadService.listMy({ page_number: 1, page_size: LEAD_FETCH_PAGE_SIZE, order_by: "DESC" });
      setLeads(data?.customerLeads || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.text || err?.message || "Unable to load leads. Please check your network connection.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [leadService]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const filteredLeads = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return leads.filter((l) => {
      const matchSearch =
        !q || [l.title, l.description, l.city, l.state, l.purpose?.name].some((v) => v && String(v).toLowerCase().includes(q));
      const matchStatus = !selectedStatus || String(l.status || "").toUpperCase() === selectedStatus;
      const matchPurpose = !selectedPurpose || String(l.purpose?.id) === selectedPurpose;
      return matchSearch && matchStatus && matchPurpose;
    });
  }, [leads, searchTerm, selectedStatus, selectedPurpose]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / LEAD_DEFAULT_PAGE_SIZE));
  const pagedLeads = filteredLeads.slice((currentPage - 1) * LEAD_DEFAULT_PAGE_SIZE, currentPage * LEAD_DEFAULT_PAGE_SIZE);
  const hasFilters = Boolean(searchTerm || selectedStatus || selectedPurpose);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, setCurrentPage]);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedStatus("");
    setSelectedPurpose("");
    setCurrentPage(1);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await leadService.remove(leadToDelete.id);
      showToast("Lead deleted successfully", { type: "success" });
      setLeadToDelete(null);
      fetchLeads();
    } catch (err) {
      showToast(err?.response?.data?.text || err?.message || "Unable to delete lead", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const renderActions = (lead) => (
    <div className="d-flex gap-2">
      <Link to={`/customer/leads/${lead.id}`} className="btn btn-sm btn-outline-secondary">
        View
      </Link>
      <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" aria-label={`Edit ${lead.title}`} onClick={() => navigate(`/customer/leads/${lead.id}/edit`)}>
        <FiEdit2 size={14} />
      </button>
      <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" aria-label={`Delete ${lead.title}`} onClick={() => setLeadToDelete(lead)}>
        <FiTrash2 size={14} />
      </button>
    </div>
  );

  return (
    <div className="properties-module-page">
      <Notification />

      <div className="property-page-header">
        <div className="property-page-header__content">
          <div className="property-page-header__badge">
            <FiClipboard className="property-page-header__badge-icon" />
            <span>Lead Hub</span>
            {!loading && !error && <span className="property-page-header__count-tag">{leads.length} Listed</span>}
          </div>
          <h1 className="property-page-header__title">My Leads</h1>
          <p className="property-page-header__description">
            Customer requirements you have created. Only leads created by your account appear here.
          </p>
        </div>
        <div className="property-page-header__actions">
          <Link to={CREATE_PATH} className="property-page-header__create-btn">
            <FiPlus size={18} />
            <span>Create Lead</span>
          </Link>
        </div>
      </div>

      <div className="property-filter-toolbar">
        <div className="property-filter-toolbar__controls">
          <form className="property-filter-toolbar__search" role="search" onSubmit={(e) => e.preventDefault()}>
            <FiSearch className="property-filter-toolbar__search-icon" />
            <input
              type="text"
              aria-label="Search leads"
              placeholder="Search title, description, purpose, city..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="property-filter-toolbar__search-input"
            />
            {searchTerm && (
              <button type="button" className="property-filter-toolbar__clear-btn" aria-label="Clear search" onClick={() => setSearchTerm("")}>
                ×
              </button>
            )}
          </form>

          <div className="property-filter-toolbar__selects">
            <div className="property-filter-select-wrap">
              <select
                aria-label="Filter by type"
                value={selectedPurpose}
                onChange={(e) => {
                  setSelectedPurpose(e.target.value);
                  setCurrentPage(1);
                }}
                className="property-filter-select"
              >
                <option value="">All Types</option>
                {purposes.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="property-filter-select-wrap">
              <select
                aria-label="Filter by status"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="property-filter-select"
              >
                <option value="">All Statuses</option>
                {LEAD_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            {hasFilters && (
              <button type="button" className="property-filter-reset-btn" onClick={resetFilters} title="Reset Filters">
                <FiRotateCcw size={14} />
                <span>Reset</span>
              </button>
            )}
          </div>

          <div className="property-view-toggle">
            <button type="button" aria-label="Grid view" className={`property-view-toggle__btn ${viewMode === "grid" ? "property-view-toggle__btn--active" : ""}`} onClick={() => setViewMode("grid")} title="Grid View">
              <FiGrid size={16} />
            </button>
            <button type="button" aria-label="List view" className={`property-view-toggle__btn ${viewMode === "table" ? "property-view-toggle__btn--active" : ""}`} onClick={() => setViewMode("table")} title="List / Table View">
              <FiList size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="properties-loading-wrap">
          <Spinner loading={true} />
          <p className="mt-3 text-muted">Loading leads...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchLeads}>
            Retry
          </button>
        </div>
      ) : pagedLeads.length === 0 ? (
        <div className="properties-empty-state">
          <div className="properties-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="properties-empty-state__title">{hasFilters ? "No Matching Leads" : "You haven't created any leads yet"}</h3>
          <p className="properties-empty-state__text">
            {hasFilters ? "Try adjusting your search terms or filters." : "Create your first customer lead to start receiving property offers."}
          </p>
          {hasFilters ? (
            <button type="button" className="btn btn-outline-success mt-2" onClick={resetFilters}>
              Clear Filters
            </button>
          ) : (
            <Link to={CREATE_PATH} className="btn btn-primary mt-2">
              Create Lead
            </Link>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="properties-grid">
          {pagedLeads.map((lead) => (
            <div key={lead.id} className="lease-card">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <span className="badge bg-primary-subtle text-primary fw-semibold">{lead.purpose?.name || "Lead"}</span>
                <StatusBadge status={lead.status} />
              </div>
              <h5 className="fw-bold text-dark mb-1 text-truncate" title={lead.title}>
                <Link to={`/customer/leads/${lead.id}`} className="text-decoration-none text-dark">
                  {lead.title || "Untitled lead"}
                </Link>
              </h5>
              <p className="text-muted small lease-card__desc">{lead.description || "No description provided."}</p>
              <div className="d-flex flex-column gap-1 small mb-3">
                <span className="fw-semibold text-dark">Budget: ₹{lead.budget != null ? Number(lead.budget).toLocaleString("en-IN") : "N/A"}</span>
                <span className="text-secondary">
                  <FiMapPin className="me-1" aria-hidden="true" />
                  {[lead.city, lead.state].filter(Boolean).join(", ") || "Location N/A"}
                </span>
                {lead.created_on && (
                  <span className="text-secondary">
                    <FiCalendar className="me-1" aria-hidden="true" />
                    {formatDate(lead.created_on)}
                  </span>
                )}
              </div>
              {renderActions(lead)}
            </div>
          ))}
        </div>
      ) : (
        <div className="table-responsive bg-white rounded-3 border">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Budget</th>
                <th>Location</th>
                <th>Created</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pagedLeads.map((lead) => (
                <tr key={lead.id}>
                  <td className="fw-semibold">
                    <Link to={`/customer/leads/${lead.id}`} className="text-decoration-none text-dark">
                      {lead.title || "Untitled lead"}
                    </Link>
                  </td>
                  <td>{lead.purpose?.name || "—"}</td>
                  <td>₹{lead.budget != null ? Number(lead.budget).toLocaleString("en-IN") : "N/A"}</td>
                  <td>{[lead.city, lead.state].filter(Boolean).join(", ") || "—"}</td>
                  <td>{formatDate(lead.created_on) || "—"}</td>
                  <td>
                    <StatusBadge status={lead.status} />
                  </td>
                  <td>{renderActions(lead)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && !loading && !error && (
        <div className="properties-pagination-bar">
          <button type="button" className="properties-page-nav-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>
          <span className="properties-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button type="button" className="properties-page-nav-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {leadToDelete && (
        <div className="lease-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Delete lead">
          <div className="lease-confirm">
            <h5 className="fw-bold">Delete lead?</h5>
            <p className="text-muted mb-4">"{leadToDelete.title || `#${leadToDelete.id}`}" will be removed from your leads.</p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setLeadToDelete(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
